// Invoices service - moved from database.js
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  db
} from '../../utils/database/firebase-config';

// Import related services
import { journalEntriesService } from './journal-entries';
import { accountMappingService } from './account-mapping';
import { chartOfAccountsService } from './chart-of-accounts';
import { clientsService } from '../logistics/clients';
import { auditService, AUDIT_MODULES, AUDIT_ACTIONS, AUDIT_TAGS } from '../system/audit';

const invoiceLabel = (i) => i?.invoiceNumber || i?.id || 'invoice';

export const invoicesService = {
  // Generate invoice number utility
  generateInvoiceNumber() {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `INV-${year}${month}-${random}`;
  },

  async getInvoices(companyId) {
    const invoicesRef = collection(db, 'invoices');
    const q = query(invoicesRef, where('companyId', '==', companyId));
    const snapshot = await getDocs(q);
    // Get all clients for this company to populate client information
    const clients = await clientsService.getClients(companyId);
    const clientsMap = new Map();
    // Create map with both Firestore document ID and custom clientId for lookup
    clients.forEach(client => {
      clientsMap.set(client.id, client); // Firestore document ID
      if (client.clientId) {
        clientsMap.set(client.clientId, client); // Custom client ID
      }
    });
    const invoices = snapshot.docs.map(doc => {
      const data = doc.data();
      // Find and populate client information
      const client = clientsMap.get(data.clientId);
      return {
        ...data,
        id: data.invoiceNumber || doc.id,  // Use invoiceNumber for display, fallback to doc ID
        docId: doc.id,  // Keep the Firebase document ID as a separate field if needed
        client: client || null,
        clientName: client?.name || client?.companyName || data.clientName || 'Unknown Customer',
        // Add computed fields for payment tracking
        totalAmount: data.finalTotal || data.total || 0,
        paidAmount: data.paidAmount || 0,
        remainingAmount: (data.finalTotal || data.total || 0) - (data.paidAmount || 0),
        paymentStatus: data.paymentStatus || data.status || 'unpaid'
      };
    });
    return invoices.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      return dateB - dateA;
    });
  },
  async getInvoice(invoiceId) {
    try {
      console.log('🔄 Getting invoice with ID:', invoiceId);
      // First try to get by Firestore document ID
      const invoiceRef = doc(db, 'invoices', invoiceId);
      const snapshot = await getDoc(invoiceRef);
      if (snapshot.exists()) {
        const data = snapshot.data();
        console.log('✅ Invoice found by document ID');
        return {
          ...data,
          id: data.invoiceNumber || data.id || snapshot.id,  // Prefer invoiceNumber, fallback to stored id, then document ID
          docId: snapshot.id  // Always keep the Firestore document ID
        };
      }
      // If not found by document ID, try to find by invoiceNumber
      console.log('🔄 Invoice not found by document ID, searching by invoice number...');
      const invoicesRef = collection(db, 'invoices');
      const q = query(invoicesRef, where('invoiceNumber', '==', invoiceId));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        console.log('✅ Invoice found by invoice number');
        return {
          ...data,
          id: data.invoiceNumber || data.id || doc.id,  // Prefer invoiceNumber, fallback to stored id, then document ID
          docId: doc.id  // Always keep the Firebase document ID
        };
      }
      console.log('❌ Invoice not found with ID:', invoiceId);
      return null;
    } catch (error) {
      console.error('❌ Error getting invoice:', error);
      throw error;
    }
  },
  async addInvoice(invoiceData) {
    try {
      console.log('🔄 Adding invoice to database...');
      const invoicesRef = collection(db, 'invoices');
      // Create the invoice document with proper ID handling
      const docRef = await addDoc(invoicesRef, {
        ...invoiceData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      console.log('✅ Invoice document created with ID:', docRef.id);
      // Update shipments to show they're invoiced (use the Firestore document ID)
      if (invoiceData.shipmentIds && invoiceData.shipmentIds.length > 0) {
        console.log('🔄 Updating shipments with invoice relationship...');
        // const updatePromises = invoiceData.shipmentIds.map(async (shipmentId) => {
        //   try {
        //     console.log(`  🚚 Updating shipment ${shipmentId} with invoice ${docRef.id}`);
        //     await updateDoc(doc(db, 'shipments', shipmentId), {
        //       invoiceId: docRef.id, // Use Firestore document ID
        //       invoiceNumber: invoiceData.invoiceNumber, // Store formatted invoice number separately
        //       invoiceStatus: 'invoiced',
        //       invoiceDate: invoiceData.issueDate,
        //       invoiceAmount: invoiceData.finalTotal || invoiceData.total,
        //       updatedAt: serverTimestamp()
        //     });
        //     console.log(`  ✅ Shipment ${shipmentId} updated successfully`);
        //   } catch (shipmentError) {
        //     console.error(`  ❌ Failed to update shipment ${shipmentId}:`, shipmentError);
        //     throw shipmentError;
        //   }
        // });
        // await Promise.all(updatePromises);
        console.log('✅ All shipments updated with invoice relationship');
      }
      // Create journal entry for the invoice (proper accounting: one entry per invoice)
      try {
        console.log('🔄 Creating journal entry for invoice...');
        console.log('📊 Invoice data for journal entry:', {
          invoiceNumber: invoiceData.invoiceNumber,
          subtotal: invoiceData.subtotal,
          vatAmount: invoiceData.vatAmount,
          total: invoiceData.finalTotal || invoiceData.total,
          createdBy: invoiceData.createdBy,
          companyId: invoiceData.companyId
        });
        // Check if journal entry already exists to prevent duplicates
        const existingJournalEntries = await journalEntriesService.getJournalEntries(invoiceData.companyId);
        const existingEntry = existingJournalEntries.find(entry => 
          entry.sourceType === 'invoice' && 
          (entry.sourceId === docRef.id || entry.reference === invoiceData.invoiceNumber)
        );
        if (existingEntry) {
          console.log('⚠️ Journal entry already exists for this invoice:', existingEntry.id);
          console.log('🎉 Invoice creation completed successfully (journal entry already exists)');
          return docRef.id;
        }
        // Ensure we have createdBy field
        if (!invoiceData.createdBy) {
          console.warn('⚠️ No createdBy field found, using system');
          invoiceData.createdBy = 'system';
        }
        // Ensure account mappings are initialized
        let accountMappings;
        try {
          accountMappings = await accountMappingService.getAccountMappings(invoiceData.companyId);
          console.log('✅ Account mappings loaded:', accountMappings);
        } catch (mappingError) {
          console.log('⚠️ Account mappings not found, initializing...');
          accountMappings = await accountMappingService.initializeDefaultMappings(invoiceData.companyId, invoiceData.createdBy);
          console.log('✅ Account mappings initialized:', accountMappings);
        }
        // Ensure chart of accounts is initialized
        let chartOfAccounts;
        try {
          chartOfAccounts = await chartOfAccountsService.getChartOfAccounts(invoiceData.companyId);
          if (chartOfAccounts.length === 0) {
            console.log('⚠️ Chart of accounts not found, initializing...');
            await chartOfAccountsService.initializeDefaultChartOfAccounts(invoiceData.companyId, invoiceData.createdBy);
            console.log('✅ Chart of accounts initialized');
          } else {
            console.log(`✅ Chart of accounts loaded: ${chartOfAccounts.length} accounts`);
          }
        } catch (chartError) {
          console.error('❌ Error with chart of accounts:', chartError);
          throw new Error(`Chart of accounts error: ${chartError.message}`);
        }
        // Create journal entry for invoice (revenue recognition)
        console.log('🔄 Calling createRevenueJournalEntry...');
        const journalId = await journalEntriesService.createRevenueJournalEntry(
          invoiceData.companyId,
          { 
            ...invoiceData, 
            id: docRef.id, 
            invoiceNumber: invoiceData.invoiceNumber,
            invoiceDate: invoiceData.issueDate,
            finalTotal: invoiceData.finalTotal || invoiceData.total,
            client: invoiceData.client || { name: 'Unknown Client' }
          },
          invoiceData.createdBy
        );
        console.log('✅ Journal entry created successfully with ID:', journalId);
      } catch (journalError) {
        console.error('❌ Journal entry creation failed:', journalError);
        // Log detailed error information to help debug
        console.error('❌ Full journal error object:', JSON.stringify(journalError, null, 2));
        // Don't fail the invoice creation if journal entry fails, but show warning
        console.warn('⚠️ Invoice created but journal entry failed. This needs to be manually corrected.');
        console.warn('⚠️ Use the "Create Missing Entries" button in Journal Entries tab to retry.');
      }
      auditService.log({
        module: AUDIT_MODULES.INVOICE,
        action: AUDIT_ACTIONS.CREATE,
        recordId: docRef.id,
        recordLabel: invoiceLabel(invoiceData),
        after: invoiceData,
        tags: [AUDIT_TAGS.ALL, AUDIT_TAGS.FINANCIAL],
        companyId: invoiceData.companyId,
        meta: {
          invoiceNumber: invoiceData.invoiceNumber,
          customerId: invoiceData.clientId,
          shipmentIds: invoiceData.shipmentIds || [],
          total: invoiceData.finalTotal || invoiceData.total
        }
      });
      console.log('🎉 Invoice creation completed successfully');
      return docRef.id; // Return the Firestore document ID
    } catch (error) {
      console.error('❌ Error in addInvoice:', error);
      throw error;
    }
  },
  async updateInvoice(invoiceId, invoiceData) {
    try {
      console.log('🔄 Updating invoice with ID:', invoiceId);
      
      // First try to get the invoice to determine the correct document ID
      let documentId = invoiceId;
      
      // If the provided ID looks like an invoice number (contains letters), try to find the document
      if (typeof invoiceId === 'string' && invoiceId.includes('-')) {
        try {
          const invoice = await this.getInvoice(invoiceId);
          if (invoice && invoice.docId) {
            documentId = invoice.docId;
            console.log('✅ Found document ID for invoice number:', documentId);
          } else {
            console.log('⚠️ Invoice not found, trying direct update with provided ID');
          }
        } catch (error) {
          console.log('⚠️ Error finding invoice, trying direct update with provided ID:', error.message);
        }
      }
      
      const invoiceRef = doc(db, 'invoices', documentId);
      let before = null;
      try {
        const snap = await getDoc(invoiceRef);
        if (snap.exists()) before = { id: snap.id, ...snap.data() };
      } catch { /* best effort */ }

      await updateDoc(invoiceRef, { ...invoiceData, updatedAt: serverTimestamp() });

      auditService.logUpdate({
        module: AUDIT_MODULES.INVOICE,
        recordId: documentId,
        recordLabel: invoiceLabel({ ...before, ...invoiceData }),
        before,
        after: { ...(before || {}), ...invoiceData },
        extraTags: [AUDIT_TAGS.FINANCIAL],
        meta: {
          invoiceNumber: before?.invoiceNumber || invoiceData.invoiceNumber,
          customerId: before?.clientId || invoiceData.clientId
        }
      });

      console.log('✅ Invoice updated successfully');
      return true;
    } catch (error) {
      console.error('❌ Error updating invoice:', error);
      throw error;
    }
  },
  async deleteInvoice(invoiceId) {
    try {
      console.log('🔄 Deleting invoice with ID:', invoiceId);
      
      // Try to delete the invoice document directly using the provided ID
      // The ID should be the Firestore document ID (docId)
      console.log('🔄 Deleting invoice document with ID:', invoiceId);
      const invoiceRef = doc(db, 'invoices', invoiceId);
      let before = null;
      try {
        const snap = await getDoc(invoiceRef);
        if (snap.exists()) before = { id: snap.id, ...snap.data() };
      } catch { /* best effort */ }

      await deleteDoc(invoiceRef);
      console.log('✅ Invoice document deleted successfully');

      auditService.log({
        module: AUDIT_MODULES.INVOICE,
        action: AUDIT_ACTIONS.DELETE,
        recordId: invoiceId,
        recordLabel: invoiceLabel(before),
        before,
        after: null,
        tags: [AUDIT_TAGS.ALL, AUDIT_TAGS.FINANCIAL, AUDIT_TAGS.DELETIONS],
        companyId: before?.companyId,
        meta: {
          invoiceNumber: before?.invoiceNumber,
          customerId: before?.clientId,
          total: before?.finalTotal || before?.total
        }
      });
      return true;
    } catch (error) {
      console.error('❌ Error deleting invoice:', error);
      throw error;
    }
  },
}; 